<template>
  <div class="package-container mb-3 p-2 rounded d-flex flex-wrap gap-2">
    <!-- Show outdated quote message for BO bookings with incorrect package codes -->
    <div v-if="showOutdatedQuoteMessage" class="outdated-quote-message">
      <span class="fw-bold text-danger">OUTDATED QUOTE IGNORE</span>
    </div>
    
    <!-- Show normal package details if not outdated -->
    <template v-else>
      <span class="fw-bold text-primary-blue me-2">
        {{ t('reservations.formula') }}:
      </span>
      <template v-for="(packageItem, index) in packageDetails" :key="index">
        <component :is="getIconComponent(packageItem)" />
        <span class="text-primary-blue">
          {{ t(`reservations.${packageItem}`) }}
        </span>
        <span
          v-if="index < packageDetails.length - 1"
          class="text-primary-blue fs-5 lh-1"
        >
          +
        </span>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, type Component } from 'vue'
  import { useI18n } from 'vue-i18n'

  // Package Icons
  import AccommodationIcon from '@/assets/AccommondationIcon.vue'
  import SkipassIcon from '@/assets/SkipassIcon.vue'
  import EquipmentIcon from '@/assets/EquipmentIcon.vue'
  import TrainIcon from '@/assets/TrainIcon.vue'
  import TransferIcon from '@/assets/TransferIcon.vue'
  import PlaneIcon from '@/assets/PlaneIcon.vue'

  import { PackageEnum } from '@/config'

  // Function to normalize package codes
  const normalizePackageCode = (code: string): PackageEnum | null => {
    if (!code) return null
    const baseCode = code.split('_')[0]
    return Object.values(PackageEnum).includes(baseCode as PackageEnum)
      ? (baseCode as PackageEnum)
      : null
  }

  const props = defineProps({
    packageCode: {
      type: String,
      required: true,
    },
    bookingReference: {
      type: String,
      required: true,
    },
  })

  const { t } = useI18n()

  // Check if we should show outdated quote message
  const showOutdatedQuoteMessage = computed(() => {
    return props.bookingReference?.startsWith('BO') && 
           props.packageCode && 
           !['PFPF', 'PFF'].includes(props.packageCode)
  })

  const packageDetails = computed((): Array<string> => {
    if (!props.packageCode) return []
    
    // Normalize the package code directly here
    const normalizedCode = normalizePackageCode(props.packageCode)

    switch (normalizedCode) {
      case PackageEnum.HS:
        return ['accommodation']
      case PackageEnum.HF:
        return ['flight', 'accommodation']
      case PackageEnum.HTT:
      case PackageEnum.HTTF:
        return ['train', 'transfer', 'accommodation']
      case PackageEnum.PF:
        return ['accommodation', 'skipass']
      case PackageEnum.PFF:
        return ['flight', 'accommodation', 'skipass']
      case PackageEnum.PFPF:
        return ['flight', 'accommodation', 'skipass', 'equipment']
      case PackageEnum.PFP:
        return ['accommodation', 'skipass', 'equipment']
      case PackageEnum.PFTT:
      case PackageEnum.PFTTNL:
        return ['train', 'transfer', 'accommodation', 'skipass']
      case PackageEnum.PFCS:
        return ['accommodation', 'skipassShort']
      case PackageEnum.PFPCS:
        return ['accommodation', 'skipass', 'equipmentShort']
      case PackageEnum.PFTTF:
      case PackageEnum.PFTTUK:
        return ['train', 'accommodation', 'skipass']
      case PackageEnum.PFPTTF:
      case PackageEnum.PFPTTNL:
      case PackageEnum.PFPTTUK:
        return ['train', 'accommodation', 'skipass', 'equipment']
      default:
        return []
    }
  })

  const getIconComponent = (item: string): Component | null => {
    const logos: Record<string, Component> = {
      accommodation: AccommodationIcon,
      skipass: SkipassIcon,
      skipassShort: SkipassIcon,
      equipment: EquipmentIcon,
      equipmentShort: EquipmentIcon,
      train: TrainIcon,
      transfer: TransferIcon,
      flight: PlaneIcon,
    }
    return logos[item] || null
  }
</script>

<style scoped>
  .package-container {
    background-color: var(--blue-100);
    border-radius: var(--border-radius-default);
    font-size: 14px;
  }

  .outdated-quote-message {
    width: 100%;
    text-align: center;
  }
</style>
